import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { randomId } from '@/core/utils/random'

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info'

export type ToastAction = {
  id: string
  label: string
  onClick: () => unknown | Promise<unknown>
  closeOnClick?: boolean
  variant?: ToastVariant
}

export type ToastItem = {
  id: string
  variant: ToastVariant
  message: string
  actions: ToastAction[]
  duration: number
  dismissible: boolean
  createdAt: number
}

type ToastState = {
  toasts: ToastItem[]
  toastMaxVisible: number
}

type ToastActions = {
  showToast: (args: {
    message: string
    variant?: ToastVariant
    duration?: number
    dismissible?: boolean
    actions?: ToastAction[]
  }) => string
  dismissToast: (id: string) => void
  clearToasts: () => void
  setToastMaxVisible: (n: number) => void
}

export const useToastStore = create<ToastState & ToastActions>()(
  devtools(
    (set) => ({
      toasts: [],
      toastMaxVisible: 4,

      setToastMaxVisible: (n) => set({ toastMaxVisible: Math.max(1, n) }),

      showToast: (args) => {
        const id = randomId()
        const item: ToastItem = {
          id,
          message: args.message,
          variant: args.variant ?? 'default',
          duration: args.duration ?? 3000,
          dismissible: args.dismissible ?? true,
          actions: args.actions ?? [],
          createdAt: Date.now(),
        }

        set((s) => {
          const next = [item, ...s.toasts]
          if (next.length > s.toastMaxVisible) next.length = s.toastMaxVisible
          return { toasts: next }
        })

        return id
      },

      dismissToast: (id) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

      clearToasts: () => set({ toasts: [] }),
    }),
    { name: 'toast-store' }
  )
)
